import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { 
  Input,
  Button,
  Avatar,
  Typography,
  Space,
  message,
  Tag
} from 'antd';
import { 
  SendOutlined,
  UserOutlined,
  PictureOutlined,
  CloseCircleFilled
} from '@ant-design/icons';

import { chatAPI, uploadAPI, healthAPI } from '../services/api';
import './ChatInterface.css';

const { TextArea } = Input;
const { Text } = Typography;

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [backendOnline, setBackendOnline] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // {file, preview, uploadedUrl}
  const abortRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // 初始欢迎消息
    const welcomeMessage = {
      id: 1,
      type: 'doctor',
      content: '您好！我是Dr. Duan，很高兴为您提供口腔正畸专业咨询服务。\n\n我可以为您解答：\n🦷 口腔正畸治疗相关问题\n📚 DSmilife正畸培训课程\n💡 正畸健康科普知识\n\n请问今天有什么可以帮助您的吗？',
      timestamp: new Date(),
      doctor: {
        name: 'Dr. Duan',
        specialty: '口腔正畸专家'
      }
    };
    setMessages([welcomeMessage]);
    // 后端健康检查
    (async () => {
      try {
        await healthAPI.check();
        setBackendOnline(true);
      } catch {
        setBackendOnline(false);
      }
    })();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (loading || typing) return;
    const text = (inputValue || '').trim();
    if (!text && !pendingImage) return;

    const idUser = Date.now();
    const userMsg = {
      id: idUser,
      type: 'user',
      content: text,
      imageUrl: pendingImage?.preview || null,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);

    // 清空输入框但保留 pendingImage 到流开始（避免闪烁）
    setInputValue('');
    setTyping(true);

    const assistantId = idUser + 1;
    setMessages(prev => [
      ...prev,
      {
        id: assistantId,
        type: 'doctor',
        content: '',
        timestamp: new Date()
      }
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await chatAPI.streamMessage({
        text,
        history: messages,
        imageUrl: pendingImage?.uploadedUrl || null,
        imageFile: null,
        onToken: (tok) => {
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId
                ? { ...m, content: (m.content || '') + tok }
                : m
            )
          );
        },
        signal: controller.signal
      });
    } catch (err) {
      console.error(err);
      message.error('发送失败');
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantId
            ? { ...m, content: '（出错：' + (err.message || '发送失败') + '）', isError: true }
            : m
        )
      );
    } finally {
      setTyping(false);
      setLoading(false);
      abortRef.current = null;
      // 发送后清理图片
      if (pendingImage?.preview) URL.revokeObjectURL(pendingImage.preview);
      setPendingImage(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    'DS中心介绍',
    '正畸护航',
    '正畸培训', 
    '培训课程',
    '正畸科普知识'
  ];

  const handleQuickQuestion = (question) => {
    setInputValue(question);
  };

  const handlePickImage = () => fileInputRef.current?.click();

  const handleImageSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const ext = file.name.toLowerCase();
      if (!/\.(png|jpg|jpeg|webp|bmp)$/.test(ext)) {
        message.error('仅支持 png/jpg/jpeg/webp/bmp');
        e.target.value = '';
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        message.error('图片需小于10MB');
        e.target.value = '';
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      // 立即上传（仅存储，不分析）
      const up = await uploadAPI.uploadImage(file);
      setPendingImage({
        file,
        preview: previewUrl,
        uploadedUrl: up.url  // 后端 /uploads/xxx
      });
      message.success('图片已添加，输入问题后发送将一起分析');
    } catch (err) {
      console.error(err);
      message.error('图片上传失败');
    } finally {
      e.target.value = '';
    }
  };

  const removePendingImage = () => {
    if (pendingImage?.preview) URL.revokeObjectURL(pendingImage.preview);
    setPendingImage(null);
  };

  // 过滤函数：识别“离线提示/自动回复”之类的系统消息
  const isOfflineNotice = (m) => {
    if (!m) return false;
    // 优先按类型识别
    if (m.type === 'notice' || m.role === 'system' || m.meta?.notice) return true;
    // 兜底按文案关键字过滤
    const txt = (m.content || '').trim();
    return /24小时随时回复/.test(txt);
  };

  const renderMessage = (msg) => {
    const isUser = msg.type === 'user';
    const isError = msg.isError;
    const rawContent = String(msg.content || '');
    // 对用户消息保持纯文本（防止XSS），对医生消息通过markdown渲染
    // 在流式过程中，markdown仍可能尚未闭合，react-markdown 能较好容错
    const markdownContent = rawContent;

    return (
      <div key={msg.id} className={`message-item ${isUser ? 'user-message' : 'doctor-message'}`}>
        <div className="message-avatar">
          {isUser ? (
            <Avatar
              size={40}
              icon={<UserOutlined />}
              style={{ backgroundColor: '#285C4D', color: 'white' }}
            />
          ) : (
            <Avatar
              size={40}
              src="/images/Dr.%20Duan.png"
            />
          )}
        </div>
        
        <div className="message-content">
          {!isUser && (
            <div className="message-header">
              <Text strong>{msg.doctor?.name || 'Dr. Duan'}</Text>
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: 8 }}>
                {msg.doctor?.specialty || '口腔正畸专家'}
              </Text>
            </div>
          )}
          
          <div className={`message-bubble ${isUser ? 'user-bubble' : 'doctor-bubble'} ${isError ? 'error-bubble' : ''}`}>
            {msg.imageUrl && (
              <img className="message-image" src={msg.imageUrl} alt="uploaded" />
            )}
            <div className="message-text markdown-body">
              {isUser ? (
                // 用户消息：保留之前的换行处理（不执行markdown解析）
                rawContent.split(/\n/).map((ln, idx, arr) => (
                  <React.Fragment key={idx}>
                    {ln === '' ? '\u00A0' : ln}
                    {idx < arr.length - 1 && <br />}
                  </React.Fragment>
                ))
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                  components={{
                    code({node, inline, className, children, ...props}) {
                      const txt = String(children);
                      if (inline) {
                        return <code className={className} {...props}>{txt}</code>;
                      }
                      return (
                        <pre className={className || 'code-block'}>
                          <code {...props}>{txt}</code>
                        </pre>
                      );
                    },
                    table({children}) {
                      return <div className="md-table-wrapper"><table>{children}</table></div>;
                    }
                  }}
                >
                  {markdownContent}
                </ReactMarkdown>
              )}
            </div>
            <div className="message-time">
              {msg.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="chat-interface">
      {/* 聊天头部 */}
      <div className="chat-header">
        <div className="chat-title">
          <Space>
            <Avatar src="/images/Dr.%20Duan.png" />
            <div>
              <Text strong>Dr. Duan</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                口腔正畸专家 · 在线咨询
              </Text>
            </div>
          </Space>
        </div>
        <Tag color={backendOnline ? 'green' : 'red'}>
          {backendOnline ? '在线' : '后端不可用'}
        </Tag>
      </div>

      {/* 消息列表 */}
      <div className="messages-container">
        <div className="messages-list">
          {messages
            .filter(m => !isOfflineNotice(m))
            .map(renderMessage)}
        </div>
      </div>

      {/* 快速问题 */}
      {messages.length <= 1 && (
        <>
          <div className="quick-questions">
            <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
              💡 您可以问我：
            </Text>
            <Space wrap>
              {quickQuestions.map((question, index) => (
                <Tag
                  key={index}
                  className="quick-question-tag"
                  onClick={() => handleQuickQuestion(question)}
                >
                  {question}
                </Tag>
              ))}
            </Space>
          </div>
        </>
      )}

      {/* 统一输入区 */}
      <div className="input-area">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleImageSelected}
        />
        {pendingImage && (
          <div className="pending-image-box">
            <img
              src={pendingImage.preview}
              alt="待分析"
              className="pending-thumb"
            />
            <Button
              size="small"
              type="text"
              icon={<CloseCircleFilled style={{ color: '#ff4d4f' }} />}
              onClick={removePendingImage}
            />
          </div>
        )}
        <Space style={{ marginBottom: 8 }}>
            <Button icon={<PictureOutlined />} onClick={handlePickImage}>
              图片
            </Button>
        </Space>
        <TextArea
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyPress}
          rows={3}
          placeholder={pendingImage ? '请输入要结合图片询问的问题（可留空直接发送图片）...' : '请输入问题或直接发送图片'}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>按 Enter 发送 · Shift+Enter 换行</Text>
          <div>
            {typing && (
              <Button style={{ marginRight: 8 }} onClick={() => { abortRef.current?.abort(); setTyping(false); }}>
                停止
              </Button>
            )}
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={typing}
              disabled={!inputValue.trim() && !pendingImage}
              onClick={sendMessage}
            >发送</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
